import { enums, type Infer, literal, object, optional, string } from "superstruct";

import getEnumValues from "../enums/getEnumValues.js";
import { parse } from "../parsing/index.js";
import Exception from "./Exception.js";

export enum ProblemType {
    RulesProblem = "RulesProblem",
}

type ProblemParams = {
    type: ProblemType;
    explanation: string;
    reason?: string;
    solution?: string;
};

export const ProblemSerializedSchema = object({
    isProblem: literal(true),
    type: enums(getEnumValues(ProblemType)),
    explanation: string(),
    reason: optional(string()),
    solution: optional(string()),
});

export type ProblemSerialized = Infer<typeof ProblemSerializedSchema>;

export class Problem extends Exception<ProblemParams> {
    constructor(explanation: string, type: ProblemType = ProblemType.RulesProblem) {
        super({ explanation, type });
        this.setMessage(explanation);
    }

    setType(type: ProblemType) {
        this.params.type = type;
        return this;
    }

    setReason(reason: string) {
        this.params.reason = reason;
        return this;
    }

    setSolution(solution: string) {
        this.params.solution = solution;
        return this;
    }

    serialize(): ProblemSerialized {
        return {
            isProblem: true,
            type: this.params.type,
            explanation: this.params.explanation,
            ...(this.params.reason ? { reason: this.params.reason } : {}),
            ...(this.params.solution ? { solution: this.params.solution } : {}),
        };
    }

    static deserialize(data: ProblemSerialized): Problem {
        const problem = new Problem(data.explanation, data.type);
        if (data.reason) problem.setReason(data.reason);
        if (data.solution) problem.setSolution(data.solution);
        return problem;
    }

    static parse(input: unknown): Problem {
        return Problem.deserialize(parse(input, ProblemSerializedSchema));
    }

    static isProblem(input: unknown): input is ProblemSerialized {
        try {
            return parse(input, ProblemSerializedSchema).isProblem === true;
        } catch {
            return false;
        }
    }
}
